/**
 * Auto Layout Service
 *
 * Automatically positions nodes in a workflow graph using a layered algorithm.
 * Handles linear flows, conditional branches, and merge points.
 */

import {
  WorkflowNodeJSON,
  WorkflowEdgeJSON,
  NodePosition,
  AUTO_LAYOUT_CONSTANTS,
  TRIGGER_NODE_TYPES,
  CONDITIONAL_NODE_TYPES,
  WorkflowNodeType,
} from '../types';

const {
  START_X,
  START_Y,
  NODE_HEIGHT,
  GAP_Y,
  BRANCH_OFFSET_X,
  MERGE_OFFSET_Y,
} = AUTO_LAYOUT_CONSTANTS;

/**
 * Node with computed layout information
 */
interface LayoutNode extends WorkflowNodeJSON {
  layer: number;
  column: number;
  branchPath?: 'true' | 'false' | 'default' | string;
}

/**
 * Result of auto-layout operation
 */
export interface AutoLayoutResult {
  nodes: WorkflowNodeJSON[];
  edges: WorkflowEdgeJSON[];
}

/**
 * Auto-layout nodes in a workflow
 */
export function autoLayoutNodes(
  nodes: WorkflowNodeJSON[],
  edges: WorkflowEdgeJSON[]
): AutoLayoutResult {
  if (nodes.length === 0) {
    return { nodes: [], edges };
  }

  // Build adjacency structures
  const { incomingMap, outgoingMap, nodeMap } = buildAdjacencyStructures(nodes, edges);

  // Find start nodes (triggers or nodes with no incoming edges)
  const startNodes = nodes.filter(
    (node) =>
      isTriggerNode(node.type) ||
      !incomingMap.has(node.id) ||
      incomingMap.get(node.id)?.length === 0
  );

  if (startNodes.length === 0) {
    // Fallback: use first node as start
    startNodes.push(nodes[0]);
  }

  // Compute layers using topological sort
  const layers = computeLayers(nodes, edges, startNodes);

  // Assign positions based on layers
  const positionedNodes = assignPositions(nodes, layers, edges, nodeMap);

  return { nodes: positionedNodes, edges };
}

/**
 * Build adjacency structures for graph traversal
 */
function buildAdjacencyStructures(
  nodes: WorkflowNodeJSON[],
  edges: WorkflowEdgeJSON[]
): {
  incomingMap: Map<string, WorkflowEdgeJSON[]>;
  outgoingMap: Map<string, WorkflowEdgeJSON[]>;
  nodeMap: Map<string, WorkflowNodeJSON>;
} {
  const incomingMap = new Map<string, WorkflowEdgeJSON[]>();
  const outgoingMap = new Map<string, WorkflowEdgeJSON[]>();
  const nodeMap = new Map<string, WorkflowNodeJSON>();

  nodes.forEach((node) => {
    nodeMap.set(node.id, node);
    incomingMap.set(node.id, []);
    outgoingMap.set(node.id, []);
  });

  edges.forEach((edge) => {
    const incoming = incomingMap.get(edge.target) || [];
    incoming.push(edge);
    incomingMap.set(edge.target, incoming);

    const outgoing = outgoingMap.get(edge.source) || [];
    outgoing.push(edge);
    outgoingMap.set(edge.source, outgoing);
  });

  return { incomingMap, outgoingMap, nodeMap };
}

/**
 * Compute layers using modified topological sort
 */
function computeLayers(
  nodes: WorkflowNodeJSON[],
  edges: WorkflowEdgeJSON[],
  startNodes: WorkflowNodeJSON[]
): Map<string, number> {
  const layers = new Map<string, number>();
  const visited = new Set<string>();
  const queue: Array<{ nodeId: string; layer: number }> = [];

  // Start with trigger/start nodes at layer 0
  startNodes.forEach((node) => {
    queue.push({ nodeId: node.id, layer: 0 });
  });

  // Build outgoing edges map
  const outgoingMap = new Map<string, WorkflowEdgeJSON[]>();
  nodes.forEach((node) => outgoingMap.set(node.id, []));
  edges.forEach((edge) => {
    const existing = outgoingMap.get(edge.source) || [];
    existing.push(edge);
    outgoingMap.set(edge.source, existing);
  });

  // BFS to assign layers
  while (queue.length > 0) {
    const { nodeId, layer } = queue.shift()!;

    if (visited.has(nodeId)) {
      // If already visited, update layer to max
      const currentLayer = layers.get(nodeId) || 0;
      layers.set(nodeId, Math.max(currentLayer, layer));
      continue;
    }

    visited.add(nodeId);
    layers.set(nodeId, layer);

    // Add neighbors
    const outgoing = outgoingMap.get(nodeId) || [];
    outgoing.forEach((edge) => {
      queue.push({ nodeId: edge.target, layer: layer + 1 });
    });
  }

  // Handle disconnected nodes
  nodes.forEach((node) => {
    if (!layers.has(node.id)) {
      // Find a reasonable layer based on connections
      layers.set(node.id, nodes.length);
    }
  });

  return layers;
}

/**
 * Assign positions to nodes based on layers
 */
function assignPositions(
  nodes: WorkflowNodeJSON[],
  layers: Map<string, number>,
  edges: WorkflowEdgeJSON[],
  nodeMap: Map<string, WorkflowNodeJSON>
): WorkflowNodeJSON[] {
  // Group nodes by layer
  const nodesByLayer = new Map<number, WorkflowNodeJSON[]>();

  nodes.forEach((node) => {
    const layer = layers.get(node.id) || 0;
    const existing = nodesByLayer.get(layer) || [];
    existing.push(node);
    nodesByLayer.set(layer, existing);
  });

  // Sort layers
  const sortedLayers = Array.from(nodesByLayer.keys()).sort((a, b) => a - b);

  // Track positions for conditional branches
  const branchOffsets = new Map<string, { trueX: number; falseX: number }>();
  const nodePositions = new Map<string, NodePosition>();

  // Build parent map to track branch paths
  const parentEdges = new Map<string, WorkflowEdgeJSON[]>();
  edges.forEach((edge) => {
    const existing = parentEdges.get(edge.target) || [];
    existing.push(edge);
    parentEdges.set(edge.target, existing);
  });

  // Assign positions layer by layer
  sortedLayers.forEach((layerIndex) => {
    const layerNodes = nodesByLayer.get(layerIndex) || [];
    const y = START_Y + layerIndex * (NODE_HEIGHT + GAP_Y);

    // Sort nodes within layer to maintain consistent ordering
    layerNodes.sort((a, b) => {
      // Sort by branch path first
      const aParents = parentEdges.get(a.id) || [];
      const bParents = parentEdges.get(b.id) || [];

      const aHandle = aParents[0]?.sourceHandle || '';
      const bHandle = bParents[0]?.sourceHandle || '';

      if (aHandle === 'true' && bHandle === 'false') return -1;
      if (aHandle === 'false' && bHandle === 'true') return 1;

      return a.id.localeCompare(b.id);
    });

    // Calculate x positions for nodes in this layer
    let currentX = START_X;

    if (layerNodes.length === 1) {
      // Single node - center it
      nodePositions.set(layerNodes[0].id, { x: START_X, y });
    } else {
      // Multiple nodes - distribute horizontally
      const totalWidth = (layerNodes.length - 1) * BRANCH_OFFSET_X;
      currentX = START_X - totalWidth / 2;

      layerNodes.forEach((node, index) => {
        // Check if this node is part of a branch
        const parents = parentEdges.get(node.id) || [];
        let x = currentX + index * BRANCH_OFFSET_X;

        // Adjust position based on branch
        if (parents.length > 0) {
          const parent = parents[0];
          const parentPos = nodePositions.get(parent.source);

          if (parentPos && isConditionalNode(nodeMap.get(parent.source)?.type)) {
            if (parent.sourceHandle === 'true') {
              x = parentPos.x - BRANCH_OFFSET_X / 2;
            } else if (parent.sourceHandle === 'false') {
              x = parentPos.x + BRANCH_OFFSET_X / 2;
            }
          }
        }

        nodePositions.set(node.id, { x, y });
      });
    }
  });

  // Apply positions to nodes
  return nodes.map((node) => {
    const position = nodePositions.get(node.id) || { x: START_X, y: START_Y };
    return {
      ...node,
      position,
    };
  });
}

/**
 * Check if a node type is a trigger
 */
function isTriggerNode(nodeType: string | undefined): boolean {
  if (!nodeType) return false;
  return TRIGGER_NODE_TYPES.has(nodeType as WorkflowNodeType);
}

/**
 * Check if a node type is conditional
 */
function isConditionalNode(nodeType: string | undefined): boolean {
  if (!nodeType) return false;
  return CONDITIONAL_NODE_TYPES.has(nodeType as WorkflowNodeType);
}

/**
 * Simple auto-layout for linear workflows (no branching)
 */
export function autoLayoutLinear(nodes: WorkflowNodeJSON[]): WorkflowNodeJSON[] {
  return nodes.map((node, index) => ({
    ...node,
    position: {
      x: START_X,
      y: START_Y + index * (NODE_HEIGHT + GAP_Y),
    },
  }));
}

/**
 * Check if nodes need layout (any node missing position)
 */
export function needsAutoLayout(nodes: WorkflowNodeJSON[]): boolean {
  return nodes.some(
    (node) =>
      !node.position ||
      !Number.isFinite(node.position.x) ||
      !Number.isFinite(node.position.y)
  );
}

/**
 * Fill in missing positions while preserving existing ones
 */
export function fillMissingPositions(
  nodes: WorkflowNodeJSON[],
  edges: WorkflowEdgeJSON[]
): WorkflowNodeJSON[] {
  // Get all nodes that already have positions
  const positionedNodes = nodes.filter(
    (n) =>
      n.position &&
      typeof n.position.x === 'number' &&
      typeof n.position.y === 'number'
  );

  // Get all nodes that need positions
  const unpositionedNodes = nodes.filter(
    (n) =>
      !n.position ||
      typeof n.position.x !== 'number' ||
      typeof n.position.y !== 'number'
  );

  if (unpositionedNodes.length === 0) {
    return nodes;
  }

  // If all nodes need positions, do full auto-layout
  if (positionedNodes.length === 0) {
    return autoLayoutNodes(nodes, edges).nodes;
  }

  // Find max Y of positioned nodes
  const maxY = Math.max(...positionedNodes.map((n) => n.position.y));

  // Position unpositioned nodes below existing ones
  const result = [...positionedNodes];
  let currentY = maxY + NODE_HEIGHT + GAP_Y;

  unpositionedNodes.forEach((node) => {
    result.push({
      ...node,
      position: { x: START_X, y: currentY },
    });
    currentY += NODE_HEIGHT + GAP_Y;
  });

  return result;
}
